import React from 'react';
import PropTypes from 'prop-types';

const SvgIcon = ({ name, ...otherProps }) => {
  // ACCESSIBILITY CHECK:
  // Default the role to "graphics-symbol", which will require an aria-labelledby, aria-label, or title attribute.
  // If the icon is purely decorative, pass in the "aria-hidden" attribute. See an example on the README.
  // For more info on SVG accessibility, See:
  // - https://accessibilityinsights.io/info-examples/web/svg-img-alt/
  // - https://www.unimelb.edu.au/accessibility/techniques/accessible-svgs
  const { role } = otherProps;
  if (!role && !otherProps['aria-hidden']) {
    if (
      otherProps['aria-label'] ||
      otherProps['aria-labelledby'] ||
      otherProps['title']
    ) {
      otherProps.role = 'graphics-symbol';
    } else {
      console.log(`Missing label for non-decorative SVG Icon ${name}`);
    }
  }

  switch (name) {
    case 'arrowRight':
      return <ArrowRight {...otherProps} />;
    case 'angleLeft':
      return <AngleLeft {...otherProps} />;
    case 'angleRight':
      return <AngleRight {...otherProps} />;
    case 'angleBottom':
      return <AngleBottom {...otherProps} />;
    case 'minus':
      return <Minus {...otherProps} />;
    case 'plus':
      return <Plus {...otherProps} />;
    case 'close':
      return <Close {...otherProps} />;
    default:
      console.error(`Unknown SVG Icon ${name}`);
  }
  return null;
};

const svgPropTypes = {
  fill: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};

const svgDefaultProps = {
  fill: '#5672C4',
  width: 21,
  height: 21,
};

SvgIcon.propTypes = {
  name: PropTypes.string.isRequired,
  role: PropTypes.string,
  ...svgPropTypes,
};
export default SvgIcon;

// React SVG definitions. Read the README for information on how to add a new icon.
// We might want to add them to individual files at some point and import them instead of defining them all here...

const ArrowRight = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="4 4 16 16" {...props}>
    <path
      fillRule="evenodd"
      d="M4 13h12.17l-5.59 5.59L12 20l8-8-8-8-1.41 1.41L16.17 11H4v2Z"
      clipRule="evenodd"
    />
    <mask
      id="a"
      width={16}
      height={16}
      x={4}
      y={4}
      maskUnits="userSpaceOnUse"
      style={{ maskType: 'luminance' }}
    >
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M4 13h12.17l-5.59 5.59L12 20l8-8-8-8-1.41 1.41L16.17 11H4v2Z"
        clipRule="evenodd"
      />
    </mask>
  </svg>
);
ArrowRight.propTypes = svgPropTypes;
ArrowRight.defaultProps = svgDefaultProps;

const AngleLeft = props => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="15 11.25 13.89 22.5"
    {...props}
  >
    <path
      fillRule="evenodd"
      d="M28.894 31.106 20.306 22.5l8.588-8.606-2.644-2.644L15 22.5l11.25 11.25 2.644-2.644Z"
      clipRule="evenodd"
    />
    <mask
      id="a"
      width={14}
      height={23}
      x={15}
      y={11}
      maskUnits="userSpaceOnUse"
      style={{ maskType: 'luminance' }}
    >
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M28.894 31.106 20.306 22.5l8.588-8.606-2.644-2.644L15 22.5l11.25 11.25 2.644-2.644Z"
        clipRule="evenodd"
      />
    </mask>
  </svg>
);
AngleLeft.propTypes = svgPropTypes;
AngleLeft.defaultProps = { ...svgDefaultProps, width: 14 };

const AngleRight = props => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="16.11 11.75 13.89 22.5"
    {...props}
  >
    <path
      fillRule="evenodd"
      d="M16.106 14.394 24.694 23l-8.588 8.606 2.644 2.644L30 23 18.75 11.75l-2.644 2.644Z"
      clipRule="evenodd"
    />
    <mask
      id="a"
      width={14}
      height={24}
      x={16}
      y={11}
      maskUnits="userSpaceOnUse"
      style={{
        maskType: 'luminance',
      }}
    >
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M16.106 14.394 24.694 23l-8.588 8.606 2.644 2.644L30 23 18.75 11.75l-2.644 2.644Z"
        clipRule="evenodd"
      />
    </mask>
  </svg>
);
AngleRight.prototypes = svgPropTypes;
AngleRight.defaultProps = svgDefaultProps;

const AngleBottom = props => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0.59 12 7.41"
    {...props}
  >
    <path
      fillRule="evenodd"
      d="M1.41.59 6 5.17 10.59.59 12 2 6 8 0 2 1.41.59Z"
      clipRule="evenodd"
    />
  </svg>
);
AngleBottom.prototypes = svgPropTypes;
AngleBottom.defaultProps = { ...svgDefaultProps, height: 14 };

const Minus = props => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0.8 0.56 22.4 2.87"
    {...props}
  >
    <path d="M.797.564h22.402v2.872H.797V.564Z" />
  </svg>
);
Minus.propTypes = svgPropTypes;
Minus.defaultProps = svgDefaultProps;

const Plus = props => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0.63 0.29 20.37 20.41"
    {...props}
  >
    <path d="M.629 12.15V8.87h8.516V.294h3.34v8.574H21v3.281h-8.516v8.555h-3.34V12.15H.63Z" />
  </svg>
);
Plus.propTypes = svgPropTypes;
Plus.defaultProps = svgDefaultProps;

const Close = props => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="6.25 6.25 17.5 17.5"
    {...props}
  >
    <path
      fillRule="evenodd"
      d="M23.75 8.012 21.988 6.25 15 13.238 8.012 6.25 6.25 8.012 13.238 15 6.25 21.988l1.762 1.762L15 16.762l6.988 6.988 1.762-1.762L16.762 15l6.988-6.988Z"
      clipRule="evenodd"
    />
    <mask
      id="a"
      width={18}
      height={18}
      x={6}
      y={6}
      maskUnits="userSpaceOnUse"
      style={{ maskType: 'luminance' }}
    >
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M23.75 8.012 21.988 6.25 15 13.238 8.012 6.25 6.25 8.012 13.238 15 6.25 21.988l1.762 1.762L15 16.762l6.988 6.988 1.762-1.762L16.762 15l6.988-6.988Z"
        clipRule="evenodd"
      />
    </mask>
  </svg>
);
Close.propTypes = svgPropTypes;
Close.defaultProps = svgDefaultProps;
